import { useSyncExternalStore } from 'react';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

// Singleton: one global `monitorForElements` subscription shared by all
// consumers so heavy lists cost one Pragmatic subscription instead of N.
let isActive = false;
const listeners = new Set<() => void>();
let cleanup: (() => void) | null = null;

const ensureMonitor = (): void => {
  if (cleanup) return;
  cleanup = monitorForElements({
    onDragStart: () => {
      if (isActive) return;
      isActive = true;
      listeners.forEach((listener) => listener());
    },
    onDrop: () => {
      if (!isActive) return;
      isActive = false;
      listeners.forEach((listener) => listener());
    }
  });
};

const subscribe = (listener: () => void): (() => void) => {
  ensureMonitor();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    // Monitor stays alive after listeners empties — view switches mount/unmount
    // consumers in bursts; keeping one global subscription avoids thrash.
  };
};

const getSnapshot = (): boolean => isActive;
const getServerSnapshot = (): boolean => false;

/**
 * Imperative read, for code that runs outside React's render cycle — e.g. work
 * deferred to a later frame that must not act on a drag that has since started.
 * Prefer the hook anywhere the answer should re-render something.
 */
export const isAnyDragActiveNow = (): boolean => {
  ensureMonitor();
  return isActive;
};

// Inert store for disabled consumers: no listener registered, so a drag starting
// or ending doesn't re-render them.
const noopSubscribe = () => () => {};
const getDisabledSnapshot = (): boolean => false;

/**
 * True while any Pragmatic DnD drag is in flight; use to suppress hover-only UI
 * (tooltips, overlays) since HTML5 DnD suppresses `mouseleave` mid-drag.
 *
 * Every drag start and drop notifies all subscribers synchronously, so on a long
 * list a component that subscribes per row makes the whole list re-render inside
 * the drag event — delaying the paint the drop feedback is waiting for. Pass
 * `enabled` to subscribe only where the answer can change what is rendered
 * (typically: only the hovered row).
 */
export const useIsAnyDragActive = (enabled = true): boolean =>
  useSyncExternalStore(
    enabled ? subscribe : noopSubscribe,
    enabled ? getSnapshot : getDisabledSnapshot,
    getServerSnapshot
  );
